import { useEffect, useRef } from "react";

const HUBS = [
    { lat: 40.7, lon: -74 },
    { lat: 51.5, lon: 0 },
    { lat: 50.1, lon: 8.7 },
    { lat: 25.2, lon: 55.3 },
    { lat: 1.35, lon: 103.8 },
    { lat: 35.7, lon: 139.7 },
];

/* Rotating wireframe globe with pulsing liquidity hubs + light-trail arcs */
export default function GlobeCanvas({ className = "" }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let raf = null;
        let w = 0;
        let h = 0;
        let rot = 0;

        const project = (lat, lon, R, cx, cy) => {
            const la = (lat * Math.PI) / 180;
            const lo = (lon * Math.PI) / 180 + rot;
            return {
                x: cx + R * Math.cos(la) * Math.sin(lo),
                y: cy - R * Math.sin(la) * 0.96,
                z: R * Math.cos(la) * Math.cos(lo),
            };
        };

        const draw = () => {
            ctx.clearRect(0, 0, w, h);
            const cx = w / 2;
            const cy = h / 2;
            const R = Math.min(w, h) * 0.4;

            for (let lat = -75; lat <= 75; lat += 12) {
                for (let lon = 0; lon < 360; lon += 10) {
                    const p = project(lat, lon, R, cx, cy);
                    if (p.z <= 0) continue;
                    ctx.fillStyle = `rgba(0, 190, 255, ${0.05 + (p.z / R) * 0.26})`;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, 1.1, 0, 7);
                    ctx.fill();
                }
            }

            ctx.strokeStyle = "rgba(37, 99, 235, 0.28)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, 7);
            ctx.stroke();

            const pts = HUBS.map((hb) => ({ ...hb, p: project(hb.lat, hb.lon, R, cx, cy) })).filter(
                (o) => o.p.z > 0
            );

            for (let i = 0; i < pts.length; i++) {
                for (let j = i + 1; j < pts.length; j++) {
                    const a = pts[i].p;
                    const b = pts[j].p;
                    const mx = (a.x + b.x) / 2;
                    const my = (a.y + b.y) / 2;
                    const dx = mx - cx;
                    const dy = my - cy;
                    const dd = Math.hypot(dx, dy) || 1;
                    const lift = Math.hypot(a.x - b.x, a.y - b.y) * 0.35;
                    ctx.strokeStyle = "rgba(0, 209, 255, 0.15)";
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.quadraticCurveTo(mx + (dx / dd) * lift, my + (dy / dd) * lift, b.x, b.y);
                    ctx.stroke();
                }
            }

            const t = performance.now() / 1000;
            for (const hb of pts) {
                const { x, y } = hb.p;
                const pulse = 4 + Math.sin(t * 2 + hb.lon) * 2;
                ctx.fillStyle = "rgba(0, 209, 255, 0.95)";
                ctx.beginPath();
                ctx.arc(x, y, 2, 0, 7);
                ctx.fill();
                ctx.strokeStyle = `rgba(0, 209, 255, ${Math.max(0, 0.45 - (pulse - 2) / 16)})`;
                ctx.beginPath();
                ctx.arc(x, y, pulse + 4, 0, 7);
                ctx.stroke();
            }

            rot += 0.0022;
            if (!reduced) raf = requestAnimationFrame(draw);
        };

        const init = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        init();
        draw();
        const ro = new ResizeObserver(init);
        ro.observe(canvas);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, []);

    return <canvas ref={ref} className={`absolute inset-0 h-full w-full ${className}`} aria-hidden="true" />;
}
